import { Input, message } from "antd";
import { Field, Form, Formik } from "formik";
import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import * as Yup from "yup";
import { TRESORY_STATUS } from "../../../data/constants";
import { linkElements, setLinkingInfo } from "../../../data/slices/linking";
import { useFiltersAndDates } from "../../../hooks/useFiltersAndDate";
import { getFieldClasses } from "../../../utilities/getFieldClasses";
import Button from "../../Button";
import CustomDatePicker from "../../CustomDatePicker";
import CustomSelect from "../../CustomSelect";
import Overlay from "../../Overlay";
import HeaderOverlay from "../../Project/Header";
import Title from "../../Project/Title";

function LinkingModal({ open, handleClose, id, data }) {
  const dispatch = useDispatch();

  const { mode } = useSelector((state) => state.layout);
  const { linkStatus } = useSelector((state) => state.linking);

  const LinkingSchema = Yup.object().shape({
    dateTreso: Yup.date().required("Date de trésorerie requise"),
    status: Yup.string().required("Statut de trésorerie requis"),
  });

  const location = useLocation();

  const handleAbondon = (e) => {
    if (e && typeof e.preventDefault === "function") {
      e.preventDefault();
    }
    handleClose(id);
    data.abandon();
  };

  const { constructFilters, getStartAndEndDate, getStartAndEndDateSynthesis } =
    useFiltersAndDates();

  const { tableConstructedFilters } = constructFilters();

  const { startDate, endDate } = getStartAndEndDate();

  const { startDateSynthesis, endDateSynthesis } =
    getStartAndEndDateSynthesis();

  const relatedToElementAmount = data?.linkingInfo?.relatedToElement
    ?.linkedGroupId
    ? data?.linkingInfo?.relatedToElement?.linkedGroupSum ?? 0
    : data?.linkingInfo?.relatedToElement?.amount ?? 0;

  const elementAmount = data?.element?.linkedGroupId
    ? data?.element?.linkedGroupSum ?? 0
    : data?.element?.amount ?? 0;

  const amountToBeDisplayed = relatedToElementAmount + elementAmount;

  const formatAmountForDisplay = (amount) => {
    const numericAmount = Number(amount) || 0;
    return numericAmount.toLocaleString("fr-FR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const handleSubmit = async (
    values,
    { setSubmitting, resetForm },
    isLinkingAgain = false
  ) => {
    try {
      const linkData = {
        elementId1: data?.linkingInfo?.relatedToElement?.id,
        elementId2: data?.element?.id,
        dateTreso: values.dateTreso,
        statutTreso: values.status,
        isListing: location.pathname.includes("listing"),
        isPlancompta: false,
        project: {
          projectId: data?.projectId,
          mode: mode,
          params: {
            startDate,
            endDate,
            ...tableConstructedFilters,
          },
        },

        linkingAgain: {
          isLinkingAgain,
          elementId: data?.linkingInfo?.relatedToElement?.id,
        },
        synthesis: {
          projectId: data?.projectId,
          mode: mode,
          params: {
            startDate: startDateSynthesis,
            endDate: endDateSynthesis,
            ...tableConstructedFilters,
          },
        },
        listing: {
          params: { ...tableConstructedFilters, startDate, endDate },
          mode,
          projectId: data?.projectId,
        },
        sum: {
          projectId: data?.projectId,
          mode: mode,
        },
      };

      await dispatch(linkElements(linkData)).then((res) => {
        if (res.meta.requestStatus === "rejected") {
          message.error(res.payload.message || "Erreur lors de la liaison");
          return;
        }

        if (!isLinkingAgain && typeof data.abandon === "function") {
          data.abandon();
        }

        if (isLinkingAgain) {
          dispatch(
            setLinkingInfo({
              ...data?.linkingInfo,
              relatedToElement: {
                ...data?.linkingInfo?.relatedToElement,
                linkedGroupId: res?.payload?.linkedGroupId,
              },
            })
          );
          handleClose(id);
          data.handleLinkingAgain();
        }

        message.success("Liaison effectuée avec succès");
        handleClose(id);
        resetForm();
      });
    } catch (error) {
      console.error("Error linking elements:", error);
      setSubmitting(false);
    }
  };

  const initialValues = useMemo(
    () => ({
      dateTreso: data?.linkingInfo?.thedate ?? "",
      status: data?.linkingInfo?.status ?? "",
    }),
    [data?.linkingInfo?.thedate, data?.linkingInfo?.status]
  );

  return (
    <Overlay show={open} closable={true}>
      <HeaderOverlay showlogo>
        <Title
          isDelete
          projectTitle={
            <div style={{ width: "80%", margin: "auto" }}>
              Vous allez lier des écritures. Elles auront toutes le même statut
              et la même date de trésorerie:
            </div>
          }
          subTitle={`Création d'une liaison entre plusieurs écritures`}
        />
        <Formik
          initialValues={initialValues}
          validationSchema={LinkingSchema}
          onSubmit={(values, formikBag) =>
            handleSubmit(values, formikBag, false)
          }
          validateOnChange={false}
          validateOnBlur={false}
          enableReinitialize
        >
          {({
            values,
            errors,
            touched,
            setFieldValue,
            isSubmitting,
            handleBlur,
            setSubmitting,
            resetForm,
          }) => {
            return (
              <Form>
                <div style={{ width: "43%", margin: "auto", marginBottom: 40 }}>
                  <div style={{ marginBottom: 12 }}>
                    <CustomDatePicker
                      errors={touched.dateTreso ? errors : ""}
                      touched={touched}
                      style={{ width: "100%" }}
                      label="Date de trésorerie*"
                      name="dateTreso"
                      values={values?.dateTreso}
                      onFocus={() => {}}
                      handleBlur={handleBlur}
                      setFieldValue={setFieldValue}
                      dropdownClassName="custom-datepicker-dropdown"
                    />
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <CustomSelect
                      label="Statut de trésorerie*"
                      name="status"
                      errors={errors}
                      touched={touched}
                      value={values.status}
                      onChange={setFieldValue}
                      options={TRESORY_STATUS}
                      condition={true}
                      showCodeColor
                      showIcon
                    />
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <label className="input-text" htmlFor="amount">
                      Montant de la liaison*
                    </label>
                    <Field
                      as={Input}
                      name="amount"
                      type="text"
                      placeholder="Montant de la liaison"
                      className={getFieldClasses(
                        data?.linkingInfo?.amount,
                        true
                      )}
                      onBlur={handleBlur}
                      value={formatAmountForDisplay(amountToBeDisplayed)}
                      readOnly={true}
                    />
                    {errors.amount && touched.amount && (
                      <div className="error-displayer-form-modal">
                        {errors.amount}
                      </div>
                    )}
                  </div>
                </div>
                <div className="verify-inputs-popup">
                  <Button
                    text={"Annuler"}
                    onClick={handleAbondon}
                    className={"action-button"}
                    type="button"
                  />
                  <Button
                    text={"Valider"}
                    htmlType="submit"
                    className={"action-button agree-action-button"}
                    iconClassName={"delete-icon"}
                    disabled={isSubmitting || linkStatus === "loading"}
                  />
                </div>

                <div className="add-new-linking">
                  <Button
                    className={"action-button agree-action-button"}
                    iconClassName={"delete-icon"}
                    disabled={isSubmitting || linkStatus === "loading"}
                    onClick={async () => {
                      await handleSubmit(
                        values,
                        {
                          setSubmitting,
                          resetForm,
                        },
                        true
                      );
                    }}
                    type="button"
                    text={"Valider et ajouter une nouvelle liaison"}
                  />
                </div>
              </Form>
            );
          }}
        </Formik>
      </HeaderOverlay>
    </Overlay>
  );
}

export default LinkingModal;
